import React, {FC, useState} from "react"

import {Row, Col} from "@irati/ui/src/Layout"
import {BasketTotal, Deal} from "@irati/types"
import basket from "@irati/checkout/utils/basket"
import tracking from "@irati-tracking/perks/perks"
import {Label} from "@irati/ui/src/Typography"
import { FormattedMessage, useIntl} from "@irati/utils/lang"
import {calculateDiscount} from "../../OrderSummary/helpers/calculateDiscount"
import MobileSavingOption from "./MobileSavingOption"
import SavingOption from "./SavingOption"
import FPCashbackFormulaDetails from "./FPCashbackFormulaDetails"

interface Props {
  deal: Deal
  isMobile?: boolean
  discount: BasketTotal
  cashback: BasketTotal
  flexiPointsCashbackRate?: number
}

export enum SavingOptionEnum {
  Discount = "Discount",
  FlexiPointsCashback = "FlexiPointsCashback"
}

const SavingOptionSelect: FC<Props> = ({isMobile, discount, cashback, flexiPointsCashbackRate, deal}) => {
  const {formatMessage} = useIntl()

  const defaultSelectedOption = basket.isCashback ? SavingOptionEnum.FlexiPointsCashback : SavingOptionEnum.Discount
  const [selectedSavingOption, setSelectedSavingOption] = useState<SavingOptionEnum>(defaultSelectedOption)

  if (!basket.items.length) {
    return null
  }

  const handleSavingOptionChange = (newValue: SavingOptionEnum) => {
    setSelectedSavingOption(newValue)
    const isFlexiPointsCashback = newValue === SavingOptionEnum.FlexiPointsCashback

    basket.isCashback = isFlexiPointsCashback
    if (isFlexiPointsCashback) {
      tracking.clickGetFlexiPointsOption(deal)
    } else {
      tracking.clickGetDiscountOption(deal)
    }
  }

  const {discountValueInCents, discountValue, flexiPointsAmount} = calculateDiscount(discount, cashback)

  return (
    <Row>
      <Col md={12}>
        {isMobile ? (
          <>
            <MobileSavingOption
              type={SavingOptionEnum.Discount}
              name="mobileDiscountSavingOption"
              label={formatMessage({id: "perks.components.saving.discount"}, {percentage: discount.discountPercentage})}
              isSelected={selectedSavingOption === SavingOptionEnum.Discount}
              onSelect={handleSavingOptionChange}
              deal={deal}
              amountToPay={discount.amount}
              currency={discount.currency}
              discount={discountValueInCents}
            />
            <MobileSavingOption
              type={SavingOptionEnum.FlexiPointsCashback}
              name="mobileFlexiPointsCashbackSavingOption"
              label={formatMessage({id: "perks.components.saving.cashback"}, {value: flexiPointsAmount})}
              isSelected={selectedSavingOption === SavingOptionEnum.FlexiPointsCashback}
              onSelect={handleSavingOptionChange}
              deal={deal}
              amountToPay={discount.originalPrice}
              currency={discount.currency}
              cashbackFormulaDetails={
                deal?.increasedPointsBackPercentageDelta ? (
                  <Label>
                    <FormattedMessage id="perks.components.orderSummary.increasedFP.tooltip" />
                  </Label>
                ) : (
                  <FPCashbackFormulaDetails
                    displayTitle
                    discountValueInCents={discountValueInCents}
                    discountValue={discountValue}
                    flexiPoints={flexiPointsCashbackRate}
                    currency={discount.currency}
                    amount={flexiPointsAmount}
                  />
                )
              }
            />
          </>
        ) : (
          <>
            <SavingOption
              deal={deal}
              amountToPay={discount.amount}
              currency={discount.currency}
              discount={discountValueInCents}
              type={SavingOptionEnum.Discount}
              name="discountSavingOption"
              label={formatMessage({id: "perks.components.saving.discount"}, {percentage: discount.discountPercentage})}
              isSelected={selectedSavingOption === SavingOptionEnum.Discount}
              onSelect={handleSavingOptionChange}
            />
            <SavingOption
              deal={deal}
              amountToPay={discount.originalPrice}
              currency={discount.currency}
              type={SavingOptionEnum.FlexiPointsCashback}
              name="flexiPointsCashbackSavingOption"
              label={formatMessage({id: "perks.components.saving.cashback"}, {value: flexiPointsAmount})}
              isSelected={selectedSavingOption === SavingOptionEnum.FlexiPointsCashback}
              onSelect={handleSavingOptionChange}
              cashbackFormulaDetails={
                deal?.increasedPointsBackPercentageDelta ? (
                  <Label>
                    <FormattedMessage id="perks.components.orderSummary.increasedFP.tooltip" />
                  </Label>
                ) : (
                  <FPCashbackFormulaDetails
                    displayTitle
                    discountValueInCents={discountValueInCents}
                    discountValue={discountValue}
                    flexiPoints={flexiPointsCashbackRate}
                    currency={discount.currency}
                    amount={flexiPointsAmount}
                  />
                )
              }
            />
          </>
        )}
      </Col>
    </Row>
  )
}

export default SavingOptionSelect
